import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { FormBanner } from '../components/ui/form.js';

/**
 * Hidden operator page — not linked from the main nav. Reach it by URL
 * (`/diagnostics`). Backed by /v1/system/errors which mirrors every
 * `app.log.error/fatal` call into a 500-entry in-memory ring buffer.
 *
 * Survives only as long as the App process runs — restarts wipe the
 * buffer. The systemd journal (and `xsc-diag` on the PBX) is the
 * durable record.
 */

interface ErrorEntry {
  ts: string;
  level: number;
  level_name: string;
  message: string;
  context?: Record<string, unknown>;
  err?: {
    type: string;
    message: string;
    stack?: string;
  };
}

interface ErrorsResponse {
  data: ErrorEntry[];
  buffer_size: number;
  buffer_max: number;
}

const LEVEL_TONE: Record<string, 'warning' | 'danger'> = {
  error: 'danger',
  fatal: 'danger',
  warn: 'warning',
};

export function DiagnosticsPage(): JSX.Element {
  const qc = useQueryClient();

  const errorsQ = useQuery({
    queryKey: ['system-errors'],
    queryFn: () => api.get('/v1/system/errors?limit=200') as Promise<ErrorsResponse>,
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  const clearMut = useMutation({
    mutationFn: () => api.post('/v1/system/errors/clear', {}) as Promise<{ cleared: number }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-errors'] }),
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Diagnostics</h1>
        <p className="text-sm text-ink-muted mt-1">
          Recent error and fatal log entries from the App process.
          In-memory ring buffer — wiped on restart. The systemd journal
          on the PBX (or <code>xsc-diag</code>) is the durable record.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Errors</CardTitle>
          <div className="flex items-center gap-2">
            {errorsQ.data && (
              <span className="text-xs text-ink-muted">
                {errorsQ.data.buffer_size} of {errorsQ.data.buffer_max}
                {errorsQ.data.buffer_size === errorsQ.data.buffer_max ? ' (full)' : ''}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={() => errorsQ.refetch()}>
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm('Clear the in-memory error buffer? Entries already shown elsewhere (journal, xsc-diag) are unaffected.')) {
                  clearMut.mutate();
                }
              }}
              disabled={clearMut.isPending || (errorsQ.data?.buffer_size ?? 0) === 0}
            >
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {errorsQ.isLoading && <p className="px-4 py-3 text-sm text-ink-muted">Loading…</p>}
          {errorsQ.error && (
            <div className="m-4">
              <FormBanner message={extractDetail(errorsQ.error)} />
            </div>
          )}
          {errorsQ.data && errorsQ.data.data.length === 0 && (
            <EmptyState
              title="No errors recorded"
              description="Nothing has been logged at error or fatal level since the App started. (Or someone just clicked Clear.)"
            />
          )}
          {errorsQ.data && errorsQ.data.data.length > 0 && (
            <ul className="divide-y divide-border">
              {errorsQ.data.data.map((e, i) => (
                <ErrorRow key={`${e.ts}-${i}`} entry={e} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <p className="text-xs text-ink-muted">
        Auto-refreshes every 10 seconds. Reach this page via URL only — it isn&apos;t
        in the navigation. Bookmark <code>/diagnostics</code> if you want a
        one-click path.
      </p>
    </div>
  );
}

function ErrorRow({ entry }: { entry: ErrorEntry }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const tone = LEVEL_TONE[entry.level_name] ?? 'danger';
  const hasDetail = !!entry.err?.stack || (entry.context && Object.keys(entry.context).length > 0);

  return (
    <li className="px-4 py-3 text-sm">
      <div className="flex items-start gap-3">
        <Badge tone={tone}>{entry.level_name}</Badge>
        <div className="flex-1 min-w-0">
          <div className="font-medium break-words">
            {entry.message || <span className="text-ink-muted italic">(no message)</span>}
          </div>
          {entry.err && (
            <div className="text-xs text-danger mt-0.5">
              {entry.err.type}: {entry.err.message}
            </div>
          )}
          {entry.context && Object.keys(entry.context).length > 0 && !expanded && (
            <div className="text-xs text-ink-muted mt-0.5 truncate">
              {summarizeContext(entry.context)}
            </div>
          )}
        </div>
        <span className="text-xs text-ink-muted font-mono whitespace-nowrap">
          {new Date(entry.ts).toLocaleTimeString()}
        </span>
        {hasDetail && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-ink-muted hover:text-ink"
          >
            {expanded ? 'Hide' : 'Detail'}
          </button>
        )}
      </div>
      {expanded && hasDetail && (
        <div className="mt-2 ml-1 space-y-2">
          {entry.context && Object.keys(entry.context).length > 0 && (
            <pre className="text-[11px] bg-surface-alt border border-border rounded p-2 overflow-x-auto">
              {JSON.stringify(entry.context, null, 2)}
            </pre>
          )}
          {entry.err?.stack && (
            <pre className="text-[11px] bg-surface-alt border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {entry.err.stack}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

function summarizeContext(ctx: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(ctx)) {
    if (parts.length >= 4) {
      parts.push('…');
      break;
    }
    const val = typeof v === 'object' && v !== null ? '{…}' : String(v);
    parts.push(`${k}=${val}`);
  }
  return parts.join(' · ');
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
